/**
 * Local P / Local S — bám `v_local_p_futures` / `mv_local_s` + CRUD `local_p` / `local_s`.
 *
 * Migrated to /table-style template (2026-05-26):
 *   - FilterBar + Search (toàn cột) + Columns
 *   - useColumnFilters + per-column ▽ Excel-style
 *   - Drop tab-level deliveryLatest/shipmentPeriod date pickers — user dùng ▽ trên
 *     `contractDate` / `deliveryFrom` / `deliveryTo` thay thế
 *   - Drop tab-level forcedClose/isFinalized buttons — thêm column `isForcedClose`
 *     (PU) / `finalizedText` (SA) cho per-column ▽
 *   - Drop server `q` / `is_forced_close` / `is_finalized` params — BE trả full
 *     dataset (pageSize=1e6), filter local toàn bộ
 *   - Page-level dateFrom/dateTo (header Futures Control) vẫn drive BE qua
 *     `user_preferences` (futures.list_filter) — không động.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useMemo, useState, type CSSProperties } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../components/BrandState'
import ResizeHandle from '../../components/ResizeHandle'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
} from '../../components/dataList'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { titleCaseName } from '../../utils/text'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useGetLocalPFuturesQuery,
  useGetLocalSQuery,
} from '../../store/api/futuresControlApi'

import { FuturesColumnsMenu } from './FuturesColumnsMenu'
import { useFuturesColumns } from './useFuturesColumns'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  const abs = Math.abs(value)
  const formatted = Number.isInteger(value)
    ? abs.toLocaleString('en-US')
    : abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return value < 0 ? `(${formatted})` : formatted
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: string
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  /** Title-case the cell value (proper names stored UPPERCASE in DB). Display-only. */
  proper?: boolean
}

const STORAGE_COLS_LOCAL_P = 'futures.local-list.pucontract'
const STORAGE_COLS_LOCAL_S = 'futures.local-list.sacontract'

/** Cột freeze khi scroll ngang — Local P dùng contractNo, Local S dùng contractSale. */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractNo', 'contractSale'])
const FROZEN_FALLBACK_WIDTH: Record<string, number> = {
  contractNo: 140,
  contractSale: 140,
}

const COLUMNS_LOCAL_P: ColumnDef[] = [
  { key: 'contractNo', label: 'Contract No' },
  { key: 'sellerRef', label: 'Seller ref' },
  { key: 'contractDate', label: 'Contract date', kind: 'date' },
  { key: 'supplierCode', label: 'Supplier code', filter: 'checkboxes' },
  { key: 'supplierName', label: 'Supplier name', proper: true },
  { key: 'quantityKg', label: 'Quantity kg', kind: 'number', align: 'right' },
  { key: 'qualityCode', label: 'Quality code', filter: 'checkboxes' },
  { key: 'contractType', label: 'Contract type', filter: 'checkboxes' },
  { key: 'priceUnit', label: 'Price unit', filter: 'checkboxes' },
  { key: 'outrightPrice', label: 'Outright price', kind: 'number', align: 'right' },
  { key: 'futuresMonth', label: 'Futures month', filter: 'checkboxes' },
  { key: 'ptbfDiffs', label: 'PTBF diffs', kind: 'number', align: 'right' },
  { key: 'totalLot', label: 'Total lot', kind: 'number', align: 'right' },
  { key: 'lotFixed', label: 'Lot fixed', kind: 'number', align: 'right' },
  { key: 'lotToFix', label: 'Lot to fix', kind: 'number', align: 'right' },
  { key: 'futuresMonth2', label: 'Futures month 2', filter: 'checkboxes' },
  { key: 'lot', label: 'Lot', kind: 'number', align: 'right' },
  { key: 'allocated', label: 'Allocated', kind: 'number', align: 'right' },
  { key: 'balance', label: 'Balance', kind: 'number', align: 'right' },
  { key: 'dateUpdated', label: 'Date upd', kind: 'datetime' },
  { key: 'personUpdated', label: 'User upd' },
]

const NON_SUM_LOCAL_P = new Set<string>(['outrightPrice', 'ptbfDiffs'])

const COLUMNS_LOCAL_S: ColumnDef[] = [
  { key: 'contractSale', label: 'Contract sale' },
  { key: 'buyersRef', label: 'Buyers ref' },
  { key: 'contractDate', label: 'Contract date', kind: 'date' },
  { key: 'cpCode', label: 'CP code', filter: 'checkboxes' },
  { key: 'clientShort', label: 'Client short', proper: true },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'noOfBags', label: 'No. bags', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Packaging', filter: 'checkboxes' },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'certification', label: 'Certification', filter: 'checkboxes' },
  { key: 'contractType', label: 'Contract type', filter: 'checkboxes' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price unit', filter: 'checkboxes' },
  { key: 'ptbfDiffs', label: 'PTBF diffs', kind: 'number', align: 'right' },
  { key: 'futuresMonth', label: 'Futures month', filter: 'checkboxes' },
  { key: 'fixationCall', label: 'Fixation call' },
  { key: 'fx', label: 'FX', kind: 'number', align: 'right' },
  { key: 'deliveryTerms', label: 'Delivery terms', filter: 'checkboxes' },
  { key: 'deliveryFrom', label: 'Delivery from', kind: 'date' },
  { key: 'deliveryTo', label: 'Delivery to', kind: 'date' },
  { key: 'pssTerms', label: 'PSS terms', filter: 'checkboxes' },
  { key: 'paymentWeightTerms', label: 'Payment weight terms' },
  { key: 'franchise', label: 'Franchise', kind: 'number', align: 'right' },
  { key: 'paymentTerms', label: 'Payment terms' },
  { key: 'note', label: 'Note' },
  { key: 'sentContractText', label: 'Sent contract' },
  { key: 'receivedContractText', label: 'Received contract' },
  { key: 'sentPaperText', label: 'Sent paper' },
  { key: 'receivedPaperText', label: 'Received paper' },
  { key: 'futuresMonth2', label: 'Futures month 2', filter: 'checkboxes' },
  { key: 'fxAuto', label: 'FX auto', kind: 'number', align: 'right' },
  { key: 'impliedDiffs', label: 'Implied diffs', kind: 'number', align: 'right' },
  { key: 'qualityPremium', label: 'Quality premium', kind: 'number', align: 'right' },
  { key: 'certPremium', label: 'Cert premium', kind: 'number', align: 'right' },
  { key: 'packagePremium', label: 'Package premium', kind: 'number', align: 'right' },
  { key: 'freight', label: 'Freight', kind: 'number', align: 'right' },
  { key: 'logisticsCosts', label: 'Logistics costs', kind: 'number', align: 'right' },
  { key: 'financeCost', label: 'Finance cost', kind: 'number', align: 'right' },
  { key: 'g2FobDiffBulk', label: 'G2 FOB diff bulk', kind: 'number', align: 'right' },
  { key: 'totalLot', label: 'Total lot', kind: 'number', align: 'right' },
  { key: 'lotFixed', label: 'Lot fixed', kind: 'number', align: 'right' },
  { key: 'lotToFix', label: 'Lot to fix', kind: 'number', align: 'right' },
  { key: 'avgFuturesPrice', label: 'Avg futures px', kind: 'number', align: 'right' },
  { key: 'kgFixed', label: 'Kg fixed', kind: 'number', align: 'right' },
  { key: 'consignee', label: 'Consignee' },
  { key: 'deliveryFrom2', label: 'Delivery from 2', kind: 'date' },
  { key: 'deliveryTo2', label: 'Delivery to 2', kind: 'date' },
]

const NON_SUM_LOCAL_S = new Set<string>([
  'price',
  'ptbfDiffs',
  'fx',
  'franchise',
  'fxAuto',
  'impliedDiffs',
  'qualityPremium',
  'certPremium',
  'packagePremium',
  'freight',
  'logisticsCosts',
  'financeCost',
  'g2FobDiffBulk',
  'avgFuturesPrice',
])

function str(v: unknown): string {
  if (v == null || v === '') return ''
  return String(v)
}

function cellValue(row: Record<string, unknown>, col: ColumnDef): string {
  const v = row[col.key]
  if (v == null) return ''
  if (col.kind === 'date') return formatAccessDate(v)
  if (col.kind === 'datetime') return formatAccessDateTime(v)
  if (col.kind === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (typeof v === 'number') return formatAccessNumber(v)
  if (col.proper) return titleCaseName(v)
  return String(v)
}

type GridRow = Record<string, unknown> & { id: string }
type Mode = 'pucontract' | 'local-s'

function sumNumericOnPage(pageRows: GridRow[], col: ColumnDef, nonSum: Set<string>): number | null {
  if (col.kind !== 'number' || nonSum.has(col.key)) return null
  let total = 0
  let count = 0
  for (const row of pageRows) {
    const v = row[col.key]
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v
      count += 1
    }
  }
  return count > 0 ? total : null
}

interface CoreProps {
  mode: Mode
  embedded?: boolean
}

function FuturesLocalListCore({ mode, embedded: _embedded }: CoreProps) {
  const isP = mode === 'pucontract'

  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [pagination, setPagination] = useState<{ page: number; pageSize: number }>({ page: 0, pageSize: -1 })
  const {
    selectedId: selectedRowId,
    setSelectedId: setSelectedRowId,
    toggleSelect: toggleSelectRow,
  } = useRowSelection<string>()

  /* ── Server query: full dataset, local filter ───────────────────── */
  const baseQueryArgs = useMemo(
    () => ({ page: 1, pageSize: FUTURES_ALL_ROWS_PAGE_SIZE }),
    [],
  )
  const pQ = useGetLocalPFuturesQuery(baseQueryArgs, {
    skip: !isP,
    refetchOnMountOrArgChange: true,
  })
  const sQ = useGetLocalSQuery(baseQueryArgs, {
    skip: isP,
    refetchOnMountOrArgChange: true,
  })
  const { data, isLoading, isFetching, error, refetch } = isP ? pQ : sQ

  const columns = isP ? COLUMNS_LOCAL_P : COLUMNS_LOCAL_S
  const nonSum = isP ? NON_SUM_LOCAL_P : NON_SUM_LOCAL_S
  const colStorageKey = isP ? STORAGE_COLS_LOCAL_P : STORAGE_COLS_LOCAL_S

  const columnsCtrl = useFuturesColumns({
    mode: isP ? 'pucontract' : 'sacontract',
    allColumns: columns,
    storageKey: colStorageKey,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths(
    isP ? 'futures.local-list.pucontract' : 'futures.local-list.sacontract',
  )

  /* ── Base rows ──────────────────────────────────────────────────── */
  const baseRows = useMemo((): GridRow[] => {
    const items = (data?.items ?? []) as Record<string, unknown>[]
    return items.map((r, idx) => {
      const rec = r as Record<string, unknown>
      const pk = isP ? String(rec.contractNo ?? '') : String(rec.contractSale ?? '')
      return {
        ...rec,
        id: `${idx}-${pk}-${String(rec.contractDate ?? '')}`,
      }
    })
  }, [data?.items, isP])

  /* ── Filter pipeline: search ∧ per-column ▽ ─────────────────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, columns) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters, columns],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const rows = useMemo((): GridRow[] => {
    if (pagination.pageSize === -1) return filteredRows
    const start = pagination.page * pagination.pageSize
    return filteredRows.slice(start, start + pagination.pageSize)
  }, [filteredRows, pagination.page, pagination.pageSize])

  /* ── FilterBar helpers ──────────────────────────────────────────── */
  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPagination((p) => ({ ...p, page: 0 }))
    setSelectedRowId(null)
    filterPopover.close()
  }

  /* ── Per-column popover meta ────────────────────────────────────── */
  const popoverColumn = useMemo(
    () => (colPopover ? columns.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover, columns],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const totalRowValues = useMemo(() => {
    return new Map(visibleColumns.map((col) => [col.key, sumNumericOnPage(rows, col, nonSum)] as const))
  }, [rows, visibleColumns, nonSum])

  const gridKey = `${pagination.page}:${pagination.pageSize}:${filteredRows.length}:${mode}`
  const minTableWidth = visibleColumns.length * 96


  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPagination((p) => ({ ...p, page: 0 }))
            }}
            placeholder={
              isP ? 'Contract No, supplier, quality…' : 'Contract sale, client, CP…'
            }
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
          setPagination((p) => ({ ...p, page: 0 }))
        }}
        options={popoverOptions}
      />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
              {data?.total != null && (data.items?.length ?? 0) < data.total
                ? ` (cap ${FUTURES_ALL_ROWS_PAGE_SIZE} / ${data.total})`
                : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={pagination.pageSize === -1 ? 0 : pagination.page}
              onPageChange={(_, newPage) => {
                setPagination((p) => ({ ...p, page: newPage }))
                setSelectedRowId(null)
              }}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={(e) => {
                setPagination({ page: 0, pageSize: Number(e.target.value) })
                setSelectedRowId(null)
              }}
              rowsPerPageOptions={[25, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title={isP ? 'Could not load Local P' : 'Could not load Local S'}
              description="Try again or contact support if the error persists."
              action={
                <Button size="small" variant="outlined" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            />
          ) : isLoading && rows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No data" description="Adjust filters or create a new record." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          // Cho phép bôi đen + copy tên cột (header).
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) =>
                            setColPopover({ key: col.key, position })
                          }
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: GridRow) =>
                visibleColumns.map((col) => {
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  const val = cellValue(row, col)
                  const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                  const wrap = getWidth(col.key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as Record<string, unknown>)['data-item-index'])
                  const r = Number.isFinite(idx) ? rows[idx] : null
                  const rid = r?.id ?? null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rid != null && rid === selectedRowId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (!rid) return
                        toggleSelectRow(rid)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

export function FuturesLocalPListView({ embedded }: { embedded?: boolean }) {
  return <FuturesLocalListCore mode="pucontract" embedded={embedded} />
}

export function FuturesLocalSListView({ embedded }: { embedded?: boolean }) {
  return <FuturesLocalListCore mode="local-s" embedded={embedded} />
}
